(() => {
  const VERSION = '2.0.0';
  if (window.PBLogoUploadUXV2?.version === VERSION) return;

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => root ? [...root.querySelectorAll(selector)] : [];

  const style = document.createElement('style');
  style.id = 'pb-logo-upload-ux-v2-style';
  style.textContent = `
    .pb-logo-v4-dropzone{margin-top:.55rem;padding:1.2rem 1.3rem;border:1.5px dashed rgba(var(--color-foreground),.3);border-radius:9px;background:rgba(var(--color-foreground),.025);cursor:pointer;transition:border-color .15s ease,background .15s ease}
    .pb-logo-v4-dropzone:hover,.pb-logo-v4-dropzone.is-dragover{border-color:rgba(var(--color-foreground),.75);background:rgba(var(--color-foreground),.055)}
    .pb-logo-v4-dropzone strong{display:block;margin-bottom:.25rem;font-size:1.35rem}.pb-logo-v4-dropzone span{display:block;font-size:1.18rem;line-height:1.4;opacity:.72}
    .pb-logo-v4-dropzone .pb-logo-v3__file{width:100%;margin:.75rem 0 0;padding:.65rem;border:1px solid rgba(var(--color-foreground),.18);border-radius:6px;background:rgb(var(--color-background));cursor:pointer}
    .pb-logo-v4-dropzone .pb-logo-v3__file::file-selector-button{margin-right:.8rem;padding:.65rem .9rem;border:1px solid rgba(var(--color-foreground),.22);border-radius:5px;background:rgba(var(--color-foreground),.05);color:inherit;cursor:pointer}
  `;
  document.head.appendChild(style);

  const setFiles = (input, files) => {
    if (!input || !files?.length) return false;
    try {
      const dt = new DataTransfer();
      [...files].forEach(file => dt.items.add(file));
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    } catch (error) {
      console.warn('PB logo dropzone could not assign dropped file', error);
      return false;
    }
  };

  const enhanceInput = input => {
    if (!input || input.dataset.pbDropzoneEnhanced === 'true') return;
    input.dataset.pbDropzoneEnhanced = 'true';

    const zone = document.createElement('div');
    zone.className = 'pb-logo-v4-dropzone';
    zone.tabIndex = 0;
    zone.setAttribute('role', 'button');
    zone.setAttribute('aria-label', 'Upload logo artwork');

    const title = document.createElement('strong');
    title.textContent = 'Drag & drop your logo here';
    const help = document.createElement('span');
    help.textContent = 'SVG gives the best live preview. PDF and transparent PNG are also accepted.';
    zone.append(title, help);

    input.parentNode.insertBefore(zone, input);
    zone.appendChild(input);

    zone.addEventListener('click', event => {
      if (event.target === input) return;
      input.click();
    });
    zone.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        input.click();
      }
    });
    ['dragenter', 'dragover'].forEach(type => zone.addEventListener(type, event => {
      event.preventDefault();
      zone.classList.add('is-dragover');
    }));
    ['dragleave', 'drop'].forEach(type => zone.addEventListener(type, event => {
      event.preventDefault();
      zone.classList.remove('is-dragover');
    }));
    zone.addEventListener('drop', event => setFiles(input, event.dataTransfer?.files));
  };

  const trimPreviewImage = image => {
    if (!image || image.dataset.pbTrimmed === 'true' || image.dataset.pbTrimBusy === 'true') return;
    if (!String(image.src || '').startsWith('data:image/png')) return;
    image.dataset.pbTrimBusy = 'true';

    const work = () => {
      try {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) return;

        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(image, 0, 0, width, height);
        const pixels = ctx.getImageData(0, 0, width, height).data;

        let minX = width, minY = height, maxX = -1, maxY = -1;
        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const i = (y * width + x) * 4;
            if (pixels[i + 3] < 8) continue;
            minX = Math.min(minX, x); minY = Math.min(minY, y);
            maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
          }
        }
        if (maxX < minX || maxY < minY) return;

        const contentW = maxX - minX + 1;
        const contentH = maxY - minY + 1;
        if (contentW / width > .92 && contentH / height > .92) return;

        const padX = Math.max(4, Math.round(contentW * .06));
        const padY = Math.max(4, Math.round(contentH * .06));
        const sx = Math.max(0, minX - padX);
        const sy = Math.max(0, minY - padY);
        const sw = Math.min(width - sx, contentW + padX * 2);
        const sh = Math.min(height - sy, contentH + padY * 2);

        const out = document.createElement('canvas');
        out.width = sw; out.height = sh;
        out.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
        image.dataset.pbTrimmed = 'true';
        image.src = out.toDataURL('image/png');
      } catch (error) {
        console.warn('PB logo preview auto-trim skipped', error);
      } finally {
        delete image.dataset.pbTrimBusy;
      }
    };

    if (image.complete) work();
    else image.addEventListener('load', work, { once: true });
  };

  const syncFoilPlaceholder = root => {
    if (!root) return;
    const box = q(root, '[data-pb-logo-v3-preview="box"]');
    const addon = q(root, '[data-pb-logo-v3-preview="addon"]');
    const foil = q(root, '[data-foil].is-active');
    const boxFoil = q(root, '[data-box-foil-name].is-active');
    if (box && foil?.dataset.foilColor) box.style.color = foil.dataset.foilColor;
    if (addon && boxFoil?.dataset.boxFoilColor) addon.style.color = boxFoil.dataset.boxFoilColor;
  };

  const scan = root => {
    qa(root || document, '.pb-logo-v3__file').forEach(enhanceInput);
    qa(root || document, '[data-pb-logo-v3-preview] img').forEach(trimPreviewImage);
    qa(document, '[id^="PBPhotoBoxCustomizer-"], [id^="PBUSBBoxCustomizer-"], [id^="PBStandaloneUSBCustomizer-"]').forEach(syncFoilPlaceholder);
  };

  scan(document);
  new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
        trimPreviewImage(mutation.target);
        return;
      }
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        if (node.matches('.pb-logo-v3__file')) enhanceInput(node);
        qa(node, '.pb-logo-v3__file').forEach(enhanceInput);
        if (node.matches('[data-pb-logo-v3-preview] img')) trimPreviewImage(node);
        qa(node, '[data-pb-logo-v3-preview] img').forEach(trimPreviewImage);
      });
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });

  document.addEventListener('click', event => {
    if (event.target instanceof Element && event.target.closest('[data-foil], [data-box-foil-name]')) {
      setTimeout(() => scan(document), 0);
    }
  }, true);

  window.PBLogoUploadUXV2 = { version: VERSION, active: true, refresh: () => scan(document) };
})();
